import { flow, pick, omit, curry, merge, omitBy } from 'lodash-es';
import {
  FIELD,
  MOBILE_RELATIVE_FIELDS,
  PHONE_RELATIVE_FIELDS,
  RECIPIENT_RELATIVE_FIELDS,
} from './constants/field';
import {
  NOTIFICATION_TYPE,
  NOTIFICATION_TYPE_TOPIC_MAP,
} from '../../../../constants/benchat';
import benchatService from '../../../../service/benchatService';
import { getFilteredBirthday } from '../../../../utils/birthday';

export const transformBirthdayFromData = (userSetting, userData) => {
  const { birth_year, birth_month, birth_day, ...restUserData } = userData;
  return {
    ...restUserData,
    [FIELD.BIRTHDAY]: getFilteredBirthday({
      birthdayFormat: userSetting.birthday_format,
      year: birth_year,
      month: birth_month,
      date: birth_day,
    }),
  };
};

export const transformMobileFromData = (userData) => {
  if (!userData[FIELD.MOBILE_PHONE]) {
    return omit(userData, MOBILE_RELATIVE_FIELDS);
  }
  return userData;
};

export const transformPhoneFromData = (userData) => {
  if (!userData[FIELD.PHONE]) {
    return omit(userData, PHONE_RELATIVE_FIELDS);
  }
  // for country_code off case
  if (!userData[FIELD.PHONE_COUNTRY_CODE]) {
    return omit(userData, FIELD.PHONE_COUNTRY_CODE);
  }
  return userData;
};

export const checkMobileIsConfirmed = (userData, formData, userSetting) => {
  // if setting is OFF
  if (!userSetting.sms_verification.status) {
    return true;
  }
  const originalMobilePhone = userData[FIELD.MOBILE_PHONE] || '';
  const currentMobilePhone = formData[FIELD.MOBILE_PHONE] || '';
  return originalMobilePhone === currentMobilePhone;
};

export const filterNonDigit = (value) =>
  value.replace(/\+/, '').replace(/\D/g, '');

const transformGenderFromData = (userData) => ({
  ...userData,
  [FIELD.GENDER]: userData[FIELD.GENDER] || '',
});

export const transformUserDataToFormData = ({ userSetting, userData }) =>
  flow(
    curry(transformBirthdayFromData)(userSetting),
    transformMobileFromData,
    transformPhoneFromData,
    transformGenderFromData,
  )(userData);

export const transformEmailFromFormData = (formData) => ({
  ...formData,
  email: formData.email?.toLowerCase() || null,
});

export const transformBirthdayFromFormData = (formData) => {
  const { [FIELD.BIRTHDAY]: birthday, ...restFormData } = formData;
  return {
    ...restFormData,
    birth_year: birthday.year,
    birth_month: birthday.month,
    birth_day: birthday.date,
  };
};

export const updateExcludeSmsPlatform = (
  hasBroadcastToUnsubscribeSms,
  smsSubscriptions,
  formData,
) => {
  if (!hasBroadcastToUnsubscribeSms) {
    return formData;
  }
  const activeSmsSubscriptions = smsSubscriptions.filter(
    (smsSubscription) => smsSubscription.isActive,
  );
  if (activeSmsSubscriptions.length > 0) {
    return {
      ...formData,
      [FIELD.EXCLUDE_SMS_PLATFORM]: false,
    };
  }
  return formData;
};

export const addTimezoneOffset = (formData) => ({
  ...formData,
  [FIELD.TIME_ZONE_OFFSET]: new Date().getTimezoneOffset() / -60,
});

export const transformMobileFromFormData = (
  userSetting,
  userData,
  formData,
) => {
  if (!userSetting.sms_verification.status) {
    const hasMobile = formData[FIELD.MOBILE_PHONE];
    return {
      ...formData,
      [FIELD.MOBILE_PHONE]: hasMobile ? formData[FIELD.MOBILE_PHONE] : null,
      [FIELD.COUNTRY_CALLING_CODE]: hasMobile
        ? formData[FIELD.COUNTRY_CALLING_CODE]
        : null,
    };
  }
  return merge(
    {},
    omit(formData, MOBILE_RELATIVE_FIELDS),
    pick(userData, MOBILE_RELATIVE_FIELDS),
  );
};

export const transformPhoneFromFormData = (formData) => {
  const hasPhone = formData[FIELD.PHONE];
  return {
    ...formData,
    [FIELD.PHONE]: hasPhone ? formData[FIELD.PHONE] : null,
    [FIELD.PHONE_COUNTRY_CODE]: hasPhone
      ? formData[FIELD.PHONE_COUNTRY_CODE]
      : null,
  };
};

export const transformGenderFromFormData = (formData) => {
  if (!formData[FIELD.GENDER]) {
    return omit(formData, FIELD.GENDER);
  }
  return formData;
};

export const getActiveMarketingSubscriptions = async (userId) => {
  const subscriptionResponse = await benchatService.getSubscriptions(
    userId,
    NOTIFICATION_TYPE_TOPIC_MAP[NOTIFICATION_TYPE.MARKETING],
  );
  const { subscriptions } = subscriptionResponse.data;
  return subscriptions.reduce((accumulate, current) => {
    if (!accumulate[current.platform]) {
      accumulate[current.platform] = [];
    }
    if (current.isActive) {
      accumulate[current.platform].push(current);
    }
    return accumulate;
  }, {});
};

const transformCountryCode = (hasCountryCode, formData) => {
  if (!hasCountryCode) {
    return {
      ...formData,
      [FIELD.PHONE_COUNTRY_CODE]: null,
      [FIELD.DELIVERY_ADDRESSES]: formData[FIELD.DELIVERY_ADDRESSES].map(
        (deliveryAddress) => ({
          ...deliveryAddress,
          recipient_phone_country_code: null,
        }),
      ),
    };
  }
  return formData;
};

const removeEmptyDeliveryAddressField = (formData) => ({
  ...formData,
  [FIELD.DELIVERY_ADDRESSES]: formData[FIELD.DELIVERY_ADDRESSES].map(
    (deliveryAddress) =>
      omitBy(
        deliveryAddress,
        (value, key) => !RECIPIENT_RELATIVE_FIELDS.includes(key) && !value,
      ),
  ),
});

export const transformFormDataToPayload = ({
  hasBroadcastToUnsubscribeSms,
  hasCountryCode,
  formData,
  smsSubscriptions,
  userData,
  userSetting,
}) =>
  flow(
    updateExcludeSmsPlatform,
    addTimezoneOffset,
    transformBirthdayFromFormData,
    transformEmailFromFormData,
    transformGenderFromFormData,
    curry(transformMobileFromFormData)(userSetting, userData),
    transformPhoneFromFormData,
    curry(transformCountryCode)(hasCountryCode),
    removeEmptyDeliveryAddressField,
  )(hasBroadcastToUnsubscribeSms, smsSubscriptions, formData);

export const fallbackMobilePhone = ({ userSetting, formData, userData }) => {
  if (!userSetting.sms_verification.status) {
    return formData;
  }
  const currentMobile = formData[FIELD.MOBILE_PHONE] || '';
  const userMobile = userData[FIELD.MOBILE_PHONE] || '';
  return {
    ...formData,
    [FIELD.MOBILE_PHONE]: currentMobile || userMobile,
  };
};
